import React, { useState, useEffect } from "react";
import { EuiFormRow } from "@elastic/eui";
import _ from "lodash";
import Api from "../../../../../../lib/api";
import * as S from "../../../style";
import { generateEntityLabel } from "../../../../../../utils/jsonApiUtils";

function AbstractDropdown({
    options,
    entityOptions,
    entityObject,
    errors,
    onValueChange,
    onCreateEntity,
    selectedOptions,
    isDisabled,
    allowCreate,
    multipleSelection,
    register,
    isClearable = true,
}) {
    const [entityData, setEntityData] = useState();
    const [entityIsLoading, setLoading] = useState(false);
    const [selectedValues, setSelectedValues] = useState(
        selectedOptions ? [...selectedOptions] : []
    );
    useEffect(() => {
        setSelectedValues(selectedOptions);
    }, [selectedOptions]);

    useEffect(() => {
        if (!isDisabled) {
            getAllData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityOptions && entityOptions.url, entityOptions && entityOptions.filterResource]);

    const onChange = (selectedOptions) => {
        onValueChange(
            selectedOptions,
            entityOptions.name,
            (entityData && selectedOptions.length && entityData[`_${selectedOptions[0].value}`]) ||
            {}
        );
        setSelectedValues(selectedOptions);
    };

    async function fetchEntity(url) {
        const response = await Api.get(url);
        return response;
    }

    async function getAllData() {
        if (!entityOptions.url) {
            return;
        }

        try {
            let url;
            if (entityOptions.filterResource && entityOptions.filterWithId) {
                url = `${entityOptions.url}?${entityOptions.filterResource}${entityObject[entityOptions.filterWithId]
                    }`;
            } else if (entityOptions.filterResource) {
                url = `${entityOptions.url}?${entityOptions.filterResource}`;
            } else {
                url = entityOptions.url;
            }
            const response = await fetchEntity(url);

            if (response.entities.meta) {
                delete response.entities.meta;
            }

            setEntityData(Object.values(response.entities)[0]);
        } catch (e) {
            console.log(e);
        }
    }

    const search = async (searchValue) => {
        setLoading(true);
        setEntityData([]);

        try {
            const response = await filterData(searchValue);

            setEntityData(response || []);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    const debounceSearch = _.debounce(search, 500);

    const handleSearchChange = (searchValue = "") => {
        if (searchValue === "") {
            getAllData();
            return;
        }

        debounceSearch(searchValue);
    };

    const filterData = async (value) => {
        const response = await Api.get(
            `${entityOptions.url}?filter[q]=${value}${entityOptions.filterResource ? `&${entityOptions.filterResource}` : ""
            }`
        );
        if (response.entities.meta) {
            delete response.entities.meta;
        }
        return Object.values(response.entities)[0];
    };

    const filteredOptions = (data) => {
        if (_.isNil(data)) {
            return null;
        }

        return _.map(data, (d) => ({ id: d.id, name: generateEntityLabel(d, entityOptions) }));
    };

    const onCreateOption = async (searchValue = "") => {
        const normalizedSearchValue = searchValue.trim().toLowerCase();

        if (!normalizedSearchValue) {
            return;
        }

        const newOption = {
            label: searchValue,
        };

        onCreateEntity(newOption);
    };

    return (
        <EuiFormRow
            fullWidth
            label={entityOptions.label}
            key={entityOptions.name}
            id={entityOptions.name}
            isInvalid={!_.isNil(errors) && !_.isNil(errors[entityOptions.name])}
            error={entityOptions.errorMessage}
            helpText={(entityOptions && entityOptions.helpText) || ""}
        >
            <S.StyledAbstractDropdown
                async
                fullWidth
                singleSelection={!multipleSelection && { asPlainText: true }}
                placeholder="Search"
                name={entityOptions.name}
                options={
                    options ||
                    (entityData &&
                        filteredOptions(entityData).map((d) => ({
                            value: d.id,
                            label: entityOptions.formatLabels ? _.startCase(d.name) : d.name,
                        }))) ||
                    []
                }
                selectedOptions={selectedValues || []}
                onChange={onChange}
                isLoading={entityIsLoading}
                onSearchChange={options ? undefined : handleSearchChange}
                onCreateOption={allowCreate ? onCreateOption : null}
                customOptionText={allowCreate ? "Add {searchValue}" : ""}
                isDisabled={isDisabled}
                isInvalid={!_.isNil(errors) && !_.isNil(errors[entityOptions.name])}
                autoComplete="off"
                inputRef={(componentRef) =>
                    register &&
                    componentRef &&
                    register(
                        { ...componentRef, name: entityOptions.name },
                        {
                            ...entityOptions.validation,
                        }
                    )
                }
                isClearable={isClearable}
            />
        </EuiFormRow>
    );
}

export default AbstractDropdown;
